import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { BackArrowIcon, PencilIcon, OverviewIcon, NotesIcon, FilesIcon, TrashIcon, QuestionMarkIcon, PaymentsIcon, InvoicesIcon } from "components/svg";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { getPurchaseOrder, resyncPO, updatePurchaseOrder, emailPo, getPoDisclosures, createPONote } from "api/PurchaseOrder";
import SiteMapSection from "components/SiteMapSection";
import SnapshotSelectionDisplay from "components/SnapshotSelectionDisplay";
import MaintenanceContextBanner from "components/MaintenanceContextBanner";
import UserContext from "context/User";
import { getMyCompany } from "api/Companies";
import H2 from "components/typography/H2";
import { themevals } from "theme/themevals";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import moment from "moment-timezone";
import { sendReminder } from "api/Dashboard";
import Overview from "./components/Overview";
import Notes from "./components/Notes";
import Files from "./components/Files";
import Payments from "./components/Payments";
import InvoicesList from "./components/InvoicesList";
import AcknowledgmentModal from "./components/AcknowledgmentModal";
import AcceptanceBanner from "./components/AcceptanceBanner";
import { Disclosure } from "./components/DisclosureBlock";
import ReloadIcon from "components/svg/icons/ReloadIcon";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";
import { trackActivity } from "api/ActivityTracker";
import { getInvoicesForPO } from "api/ProviderInvoice";

const PurchaseOrder = () => {
  const { user } = useContext<any>(UserContext);
  const { id } = useParams();
  const [company, setCompany] = useState<any>(null);
  const [po, setPo] = useState<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const navigate = useNavigate();
  const toast = useToast();
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const hasTrackedView = useRef(false);
  const [pendingInvoices, setPendingInvoices] = useState<any[]>([]);
  const [providerInvoiceAlert, setProviderInvoiceAlert] = useState<{ type: "approved" | "rejected"; invoice: any } | null>(null);
  const [showSendPoModal, setShowSendPoModal] = useState(false);
  const [sendingPo, setSendingPo] = useState(false);

  // MASTER_PLAN.md §3.9 #11 — PO acknowledgment gate. `disclosures`
  // renders as banner stack post-ack. `showAckModal` mounts the
  // blocking modal on first Sent-status view by a provider (not
  // staff). Staff bypass by design; the modal exists for the
  // provider record-of-acknowledgment, not staff.
  const [disclosures, setDisclosures] = useState<Disclosure[]>([]);
  const [termsAcknowledgedAt, setTermsAcknowledgedAt] = useState<string | null>(null);
  const [acceptAfterAck, setAcceptAfterAck] = useState(0);
  const [termsAcknowledgedBy, setTermsAcknowledgedBy] = useState<
    { id: string; name: string | null } | null
  >(null);
  // SESSION 45 GAP-42 — count of notes visible when the checkbox was
  // ticked in AcknowledgmentModal, snapshotted so the audit line reads
  // truthfully on subsequent views this session. Not persisted — the
  // DB doesn't record this yet, so a page reload drops it back to
  // null and the banner falls back to the shorter audit line. Adding
  // a `notesReviewedCount` column would let it survive reloads.
  const [notesReviewedCount, setNotesReviewedCount] = useState<number | null>(null);
  const [showAckModal, setShowAckModal] = useState(false);

  // Fibar install-only: local editable state for the PO supply instructions.
  const [savingSupply, setSavingSupply] = useState(false);

  // Post-send supply drift: provider (or team) can raise a question/comment
  // against a changed PO; team can resend from the banner to clear the flag.
  const [driftNoteInput, setDriftNoteInput] = useState<string>("");
  const [postingDriftNote, setPostingDriftNote] = useState<boolean>(false);
  const [driftNoteDone, setDriftNoteDone] = useState<boolean>(false);
  const [resendingFromBanner, setResendingFromBanner] = useState<boolean>(false);

  // Follow-up reminder for a sent-but-unaccepted PO.
  const [sendingReminder, setSendingReminder] = useState<boolean>(false);
  const [reminderResult, setReminderResult] = useState<string | null>(null);

  // Install-only: pickup requires a source address before the PO can be sent.
  // Returns true (and toasts) when send should be blocked.
  const supplyBlocksSend = (): boolean => {
    if (!po) return false;
    if (po.supplyMethod !== "pickup_from_fibar") return false;
    const a = po.supplySourceAddress || {};
    const hasAddr = [a.line1, a.city, a.state, a.postalCode].filter(Boolean).join(", ");
    if (hasAddr) return false;
    toast.add(
      "Add a supplier pickup location before sending this PO.",
      "var(--theme-ui-colors-danger)"
    );
    return true;
  };

  const saveSupply = async (patch: {
    supplyMethod?: string | null;
    supplySourceAddress?: any;
    onSiteLoadingRequired?: boolean;
  }) => {
    if (!po?.id) return;
    setSavingSupply(true);
    const res = await updatePurchaseOrder({ data: { id: po.id }, update: patch });
    if (res?.id) {
      setPo(res);
      toast.add("Supply instructions updated.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message || "Error updating supply instructions.", "var(--theme-ui-colors-danger)");
    }
    setSavingSupply(false);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | PO";
    _init();
  }, []);

  const _init = async () => {
    if (user.role !== null) {
      const res = await getMyCompany();
      if (res.id) {
        setCompany(res);
      }
    } else {
      setCompany(user.company);
    }

    const poRes = await getPurchaseOrder({ data: { id: id } });
    if (poRes.id) {
      setPo(poRes);

      // §3.9 #11 — fetch disclosures + acknowledgment payload in
      // parallel with the invoice banner lookup below. Modal gates
      // on provider (user.role === null) + status Sent (1) + not
      // yet acknowledged. Staff bypass entirely.
      try {
        const discRes = await getPoDisclosures({ data: { id: poRes.id } });
        const discList = Array.isArray(discRes?.disclosures) ? discRes.disclosures : [];
        setDisclosures(discList);
        setTermsAcknowledgedAt(discRes?.termsAcknowledgedAt ?? null);
        setTermsAcknowledgedBy(discRes?.termsAcknowledgedBy ?? null);

        const isProvider = user?.role === null;
        const isSent = Number(poRes.status ?? 0) === 1;
        if (isProvider && isSent && !discRes?.termsAcknowledgedAt) {
          setShowAckModal(true);
        }
      } catch (e) {
        // Non-critical; page still renders without banners/modal.
      }
      // Fetch invoices for banners
      try {
        const invRes = await getInvoicesForPO(poRes.id);
        if (invRes.success && Array.isArray(invRes.data)) {
          // Company users: pending invoice banner
          if (user?.accessLevel === "isCompany") {
            const pending = invRes.data.filter(
              (inv: any) => inv.status === "pending_approval" || inv.status === "review_needed" || inv.status === "revision_pending"
            );
            setPendingInvoices(pending);
          }
          // Provider users: approved/rejected alert
          if (user?.role === null) {
            const poStatus = Number(poRes.status ?? 0);
            const poPaymentStatus = Number(poRes.paymentStatus ?? 0);
            const poClosedOrPaid = poStatus === 5 || poPaymentStatus === 2;

            const approved = invRes.data.find((inv: any) => inv.status === "bill_created");
            // Only show rejected invoices that are still actionable:
            // exclude rejected invoices where a newer submission exists on this PO
            const rejected = invRes.data.find((inv: any) => {
              if (inv.status !== "rejected") return false;
              // If there's a newer invoice on this PO (any status other than rejected/superseded/duplicate_ignored), this rejection is stale
              const hasNewerSubmission = invRes.data.some(
                (other: any) =>
                  other.id !== inv.id &&
                  !['rejected', 'superseded', 'duplicate_ignored'].includes(other.status) &&
                  new Date(other.createdAt) > new Date(inv.createdAt)
              );
              return !hasNewerSubmission;
            });

            // Show the most recent actionable status
            // Hide approved banner when PO is closed or paid (no longer actionable)
            if (rejected && !poClosedOrPaid && (!approved || new Date(rejected.updatedAt) > new Date(approved.updatedAt))) {
              setProviderInvoiceAlert({ type: "rejected", invoice: rejected });
            } else if (approved && !poClosedOrPaid) {
              setProviderInvoiceAlert({ type: "approved", invoice: approved });
            }
          }
        }
      } catch (e) {
        // Non-critical
      }
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "purchase_orders",
          action: "view_purchase_order_details",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: poRes.id,
          metadata: null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }
  };

  const reloadPO = async () => {
    _init();
  };

  // Post-send drift: provider or team posts a question/comment on the changed
  // PO. Uses the PO notes channel; does not clear the drift flag (only a resend
  // does), so the team still sees it needs resending.
  const postDriftNote = async () => {
    if (!po?.id || !driftNoteInput.trim()) return;
    setPostingDriftNote(true);
    const res = await createPONote({ id: po.id, content: driftNoteInput.trim() });
    setPostingDriftNote(false);
    if (res?.id) {
      setDriftNoteInput("");
      setDriftNoteDone(true);
    }
  };

  // Team resends the PO from the drift banner. Reuses the send path, which
  // clears supplyChangedAfterSendAt on the backend, so the banner clears on
  // reload.
  const resendFromBanner = async () => {
    if (!po?.id) return;
    if (supplyBlocksSend()) return;
    setResendingFromBanner(true);
    const res = await emailPo({ data: { id: po.id } });
    setResendingFromBanner(false);
    if (res?.success) {
      await reloadPO();
    }
  };

  useEffect(() => {
    hasTrackedView.current = false;
    _init();
  }, [id]);

  const formatLastSync = (iso?: string) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "N/A";
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${month} ${day}${ordinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  const submitSync = async () => {
    if (!po?.id) return;
    setConfirmSubmit(true);
    const res = await resyncPO({ data: po });
    if (res?.success) {
      await reloadPO(); // refresh PO data
      toast.add("Purchase order synced.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message ? res.message : "Error syncing purchase order.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
  };

  const _confirmCancelPO = () => {
    setConfirmCancelModalOpen(true);
  };

  const _submitSendPo = async () => {
    if (!po?.id) return;
    if (supplyBlocksSend()) return;
    setSendingPo(true);
    try {
      const res = await emailPo({ data: { id: po.id } });
      if (res?.success !== false && !res?.error) {
        toast.add("Purchase order sent to provider.", "var(--theme-ui-colors-success)");
        setShowSendPoModal(false);
        await reloadPO();
      } else {
        toast.add(res?.message || res?.error || "Error sending purchase order.", "var(--theme-ui-colors-danger)");
      }
    } catch (e: any) {
      toast.add(e?.message || "Error sending purchase order.", "var(--theme-ui-colors-danger)");
    }
    setSendingPo(false);
  };

  const _cancelConfirmModal = () => {
    setConfirmCancelModalOpen(false);
  };

  const _submitCancelPO = async () => {
    setSubmitting(true);

    const updateData = {
      status: 6,
    };

    const res = await updatePurchaseOrder({ data: { id }, update: updateData });
  };

  return (
    <>
      <Header></Header>
      <Page
        backAction={
          <Link variant="back" href="/purchase-orders">
            <BackArrowIcon />
            {`Back to ${"PO's"}`}
          </Link>
        }
        $noPadding={true}
      >
        {po == null ? (
          <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : (
          <>
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              <Box>
                <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                  PO Details
                </H5>
              </Box>
              <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
                <Flex sx={{ gap: "40px" }}>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "overview" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("overview");
                      setSearchParams({ tab: "overview" });
                    }}
                  >
                    <OverviewIcon fill={activeTab == "overview" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "overview" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Overview
                    </Box>
                  </Flex>
                  {/*
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "versions" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("versions");
                      setSearchParams({ tab: "versions" });
                    }}
                  >
                    <VersionIcon fill={activeTab == "versions" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "versions" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Versions
                    </Box>
                  </Flex>
                  */}
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "notes" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("notes");
                      setSearchParams({ tab: "notes" });
                    }}
                  >
                    <NotesIcon fill={activeTab == "notes" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "notes" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Notes
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "payments" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("payments");
                      setSearchParams({ tab: "payments" });
                    }}
                  >
                    <PaymentsIcon fill={activeTab == "payments" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "payments" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Payments
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "files" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("files");
                      setSearchParams({ tab: "files" });
                    }}
                  >
                    <FilesIcon fill={activeTab == "files" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "files" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Files
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "invoices" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      setActiveTab("invoices");
                      setSearchParams({ tab: "invoices" });
                    }}
                  >
                    <InvoicesIcon fill={activeTab == "invoices" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "invoices" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Invoices
                    </Box>
                  </Flex>
                </Flex>
              </Flex>
            </Grid>
            {/* Pending invoice banner — company users only, hide when on Invoices tab */}
            {pendingInvoices.length > 0 && user?.accessLevel === "isCompany" && activeTab !== "invoices" && (
              <Box
                sx={{
                  backgroundColor: "#FFF3CD",
                  border: "1px solid #FFEEBA",
                  borderRadius: "6px",
                  padding: "12px 16px",
                  margin: "0 0 0 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginBottom="2px" marginTop="0px" color="#856404">
                    {pendingInvoices.length === 1
                      ? "1 provider invoice awaiting review"
                      : `${pendingInvoices.length} provider invoices awaiting review`}
                  </P>
                  {pendingInvoices.some((inv: any) => Math.abs(Number(inv.variancePct || 0)) > 5) && (
                    <P fontFamily="Poppins" fontSize="12px" marginBottom="0px" marginTop="0px" color="#856404">
                      Includes invoices with variance from PO total
                    </P>
                  )}
                </Box>
                <BasicButton
                  scheme="secondaryOutline"
                  onClick={() => {
                    setActiveTab("invoices");
                    setSearchParams({ tab: "invoices" });
                  }}
                  styles={{ padding: "4px 12px", fontSize: "12px" }}
                >
                  Review
                </BasicButton>
              </Box>
            )}
            {/* Provider invoice status alert — hide when on Invoices tab */}
            {providerInvoiceAlert && user?.role === null && activeTab !== "invoices" && (
              <Box
                sx={{
                  backgroundColor: providerInvoiceAlert.type === "approved" ? "#DDE8D2" : "#FFF3CD",
                  border: `1px solid ${providerInvoiceAlert.type === "approved" ? "#C5D6B4" : "#FFEEBA"}`,
                  borderRadius: "6px",
                  padding: "12px 16px",
                  margin: "0 0 0 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginBottom="2px" marginTop="0px" color={providerInvoiceAlert.type === "approved" ? "#3D5A20" : "#856404"}>
                    {providerInvoiceAlert.type === "approved"
                      ? "Your invoice has been approved"
                      : "Your invoice was rejected"}
                  </P>
                  <P fontFamily="Poppins" fontSize="12px" marginBottom="0px" marginTop="0px" color={providerInvoiceAlert.type === "approved" ? "#3D5A20" : "#856404"}>
                    {providerInvoiceAlert.type === "approved"
                      ? "Invoice " + (providerInvoiceAlert.invoice.invoiceNumber || "") + " for " + Number(providerInvoiceAlert.invoice.amount || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }) + " has been approved and a bill has been created."
                      : providerInvoiceAlert.invoice.notes || "Please review and resubmit."}
                  </P>
                </Box>
                <BasicButton
                  scheme="secondaryOutline"
                  onClick={() => {
                    setActiveTab("invoices");
                    setSearchParams({ tab: "invoices" });
                  }}
                  styles={{ padding: "4px 12px", fontSize: "12px" }}
                >
                  {providerInvoiceAlert.type === "rejected" ? "Resubmit" : "View"}
                </BasicButton>
              </Box>
            )}
            {/* Post-send supply-drift banner. Shows when supply changed after
                this PO was sent. Team sees an actionable version (resend to
                push the current details + clear the flag). Provider sees a
                neutral notice plus a box to raise a question/comment instead of
                being forced to re-accept. */}
            {po?.supplyChangedAfterSendAt && (
              <Box
                sx={{
                  backgroundColor: "#FFFAEA",
                  border: `1px solid ${themevals.colors.warning}`,
                  borderRadius: "6px",
                  padding: "14px 16px",
                  marginBottom: "16px",
                }}
              >
                {user.accessLevel === "isProvider" ? (
                  <>
                    <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginTop="0px" marginBottom="2px" color="#856404">
                      This purchase order was updated after you received it
                    </P>
                    <P fontFamily="Poppins" fontSize="12px" marginTop="0px" marginBottom="12px" color="#856404">
                      The mulch supply details changed. Please review the current details below. If anything does not work on your end, let us know here and our team will follow up.
                    </P>
                    {driftNoteDone ? (
                      <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0px" marginBottom="0px" color="#3D5A20">
                        Thanks. Your message was sent to our team.
                      </P>
                    ) : (
                      <>
                        <textarea
                          value={driftNoteInput}
                          placeholder="Questions or issues with the update? Add a note for our team."
                          onChange={(e) => setDriftNoteInput(e.target.value)}
                          rows={3}
                          style={{
                            width: "100%", padding: "8px 10px", fontFamily: "Poppins", fontSize: "13px",
                            border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", resize: "vertical", marginBottom: "8px",
                          }}
                        />
                        <BasicButton
                          scheme="secondaryOutline"
                          type="button"
                          $submitting={postingDriftNote}
                          keepText={true}
                          onClick={postDriftNote}
                        >
                          Send note to team
                        </BasicButton>
                      </>
                    )}
                  </>
                ) : (
                  <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                    <Box>
                      <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginTop="0px" marginBottom="2px" color="#856404">
                        Supply updated since this PO was sent
                      </P>
                      <P fontFamily="Poppins" fontSize="12px" marginTop="0px" marginBottom="0px" color="#856404">
                        The supply details changed after the provider received this PO. Resend to push the current details, or check Notes for any provider response.
                      </P>
                    </Box>
                    <BasicButton
                      scheme="secondaryOutline"
                      type="button"
                      $submitting={resendingFromBanner}
                      keepText={true}
                      onClick={resendFromBanner}
                    >
                      Resend to provider
                    </BasicButton>
                  </Flex>
                )}
              </Box>
            )}
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              {/* See Projects/view.tsx: minWidth 0 + overflowWrap on the
                  sidebar column so emails and URLs wrap inside 250px. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
                {user.role !== null && Number(po.status) !== 6 && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                    <Link
                      href={"/purchase-orders/" + po.id + "/edit"}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        width: "100%", padding: "10px 0",
                        border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                        textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                        fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                      }}
                    >
                      <PencilIcon height="13px" fill="currentColor" />
                      Edit
                    </Link>
                  </Box>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Basic Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project Name</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {po.project?.projectName ? po.project?.projectName : "Instant Estimate"}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {(() => {
                        switch (po.status) {
                          case 0:
                            return (
                              <Badge as="span" variant="warning">
                                New
                              </Badge>
                            );
                          case 1:
                            return (
                              <Badge as="span" variant="info">
                                Sent
                              </Badge>
                            );
                          case 2:
                            return (
                              <Badge as="span" variant="success">
                                Accepted
                              </Badge>
                            );
                          case 3:
                            return (
                              <Badge as="span" variant="warning">
                                Billed
                              </Badge>
                            );
                          case 4:
                            return (
                              <Badge as="span" variant="danger">
                                Denied
                              </Badge>
                            );
                          case 5:
                            return (
                              <Badge as="span" variant="success">
                                Closed
                              </Badge>
                            );
                          case 6:
                            return (
                              <Badge as="span" variant="danger">
                                Cancelled
                              </Badge>
                            );
                          default:
                            return (
                              <Badge as="span" variant="warning">
                                New
                              </Badge>
                            );
                        }
                      })()}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Payment Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {(() => {
                        switch (po.paymentStatus) {
                          case 0:
                            return (
                              <Badge as="span" variant="orange">
                                Open
                              </Badge>
                            );
                          case 1:
                            return (
                              <Badge as="span" variant="info">
                                Due
                              </Badge>
                            );
                          case 2:
                            return (
                              <Badge as="span" variant="success">
                                Paid
                              </Badge>
                            );
                          case 3:
                            return (
                              <Badge as="span" variant="info">
                                Partial Paid
                              </Badge>
                            );
                          case 4:
                            return (
                              <Badge as="span" variant="danger">
                                Cancelled
                              </Badge>
                            );
                          default:
                            return (
                              <Badge as="span" variant="orange">
                                Open
                              </Badge>
                            );
                        }
                      })()}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Terms</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      Net {po.project?.client?.terms ? po.project?.client?.terms : 30}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Project #</Label>
                    <Box
                      onClick={() => {
                        navigate(`/projects/${po.project.id}`);
                      }}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {po.project?.sequence ? `GMP-${String(po.project?.sequence).padStart(5, "0")}` : "N/A"}
                      </P>
                    </Box>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>PO Date</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(po.createdAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Updated</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(po.updatedAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  {po.lastUpdateBy?.firstName && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Updated By</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {`${po.lastUpdateBy.firstName} ${po.lastUpdateBy.lastName}`}
                      </P>
                    </Flex>
                  )}
                </Box>
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Job Site
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {po.project.location?.address?.line1 && po.project.location?.address?.line1 !== po.project.location?.address?.city
                        ? po.project.location?.address?.line1 + (po.project.location?.address?.line2 ? `, ${po.project.location?.address?.line2}` : "")
                        : po.project.location?.address?.line2
                        ? po.project.location?.address?.line2
                        : ""}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {po.project.location?.address?.city}, {po.project.location?.address?.state}
                      {po.project.location?.address?.postalCode !== "null" ? ` ${po.project.location?.address?.postalCode}` : ""}
                    </P>
                  </Flex>
                </Box>
                {/* Fibar install-only: Mulch Supply instructions. Renders only when this
                    PO is an install-only job (supplyMethod seeded from the RFQ, or the
                    source RFQ carried a preference). Company users can edit method /
                    source / on-site-loading before sending; providers see it read-only. */}
                {(po.supplyMethod || po.quote?.quoteRequest?.mulchSupplyPreference) && (() => {
                  const method = po.supplyMethod as string | null;
                  const methodLabel =
                    method === "pickup_from_fibar" ? "Pickup from supplier"
                    : method === "delivery_to_site" ? "Supplier delivers to site"
                    : method === "delivery_to_yard" ? "Supplier delivers to yard"
                    : "Not set";
                  const addr = po.supplySourceAddress || {};
                  // Supply (method / pickup address / on-site loading) is owned by
                  // the PROJECT and only ever edited there; the PO reflects it
                  // read-only. Editing supply on the PO created a second control
                  // surface and caused project<->PO drift, so PO-level editing is
                  // disabled. Changes made on the project propagate down (and flag
                  // sent POs). Kept as a const so the display logic below is
                  // unchanged; only the edit affordances are gated off.
                  const canEdit = false;
                  // Pickup requires a source address before the PO is sent. Flag it
                  // when pickup is selected but no address is set (company view only).
                  const pickupAddrMissing =
                    method === "pickup_from_fibar" &&
                    !([addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "));
                  // The RFQ preference this PO was seeded from, for the seed badge.
                  const seededFromPref = po.quote?.quoteRequest?.mulchSupplyPreference as
                    | "pickup"
                    | "delivery"
                    | null
                    | undefined;

                  // Item 1 (session 21): give the provider a clear, prominent read of
                  // WHERE the material goes, without leaking the client identity
                  // (generic "Supplier"). The destination depends on the supply
                  // method. Service type + product were removed from this summary
                  // (F4) as redundant with the line items table, so they are no
                  // longer derived here.
                  const sourceAddrLine = [addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", ");
                  // A provider does not need to be told their OWN address. For
                  // delivery_to_yard the material goes to the installer's own yard;
                  // if that address matches the provider's own registered address
                  // or the resolved depot address, suppress the address for the
                  // provider (they know it) and show a note instead. Internal team
                  // and the client still see it (they DO need to know where it
                  // goes). Pickup is a place the installer must drive to, so it is
                  // always shown. delivery_to_site shows no address (job site).
                  const norm = (a: any) =>
                    [a?.line1, a?.city, a?.state, a?.postalCode]
                      .filter(Boolean)
                      .join("|")
                      .toLowerCase()
                      .replace(/\s+/g, " ")
                      .trim();
                  const providerOwnAddr = po.provider?.address || {};
                  const depotOwnAddr = (Array.isArray(po.lineitems)
                    ? po.lineitems.find((li: any) => li?.depot?.address)?.depot?.address
                    : null) || {};
                  const srcKey = norm(addr);
                  const matchesProviderOwn =
                    !!srcKey && (srcKey === norm(providerOwnAddr) || srcKey === norm(depotOwnAddr));
                  const isProviderViewer = user?.accessLevel === "isProvider";
                  // Hide the yard address only for the provider when it is their own
                  // place. Everyone else, and all non-yard methods, unaffected.
                  const hideYardAddrForProvider =
                    method === "delivery_to_yard" && isProviderViewer && matchesProviderOwn;
                  // Material location the provider needs, shown ONLY when it is a
                  // different place than the job site (which is already in the Job
                  // Site block above). Pickup -> the depot; yard -> the installer
                  // yard. For delivery_to_site the material comes to the job site,
                  // so we do NOT repeat that address here - a short note points up
                  // to the Job Site block instead.
                  const materialLocationLabel =
                    method === "pickup_from_fibar" ? "Pickup location"
                    : method === "delivery_to_yard" ? "Yard location"
                    : null;
                  const materialLocationAddr =
                    method === "pickup_from_fibar" ? sourceAddrLine
                    : method === "delivery_to_yard" ? (hideYardAddrForProvider ? "" : sourceAddrLine)
                    : "";
                  return (
                    <>
                      <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      <Box sx={{ paddingRight: "30px" }}>
                        <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "30px", marginBottom: "20px" }}>
                          <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"0px"} marginBottom={"0px"}>
                            Mulch Supply
                          </H5>
                          {seededFromPref && (
                            <Badge as="span" variant="info" sx={{ fontSize: "10px", fontWeight: 600, paddingX: "9px", paddingY: "2px" }}>
                              {`Pre-filled from RFQ: ${seededFromPref === "pickup" ? "Pickup" : "Delivery"}`}
                            </Badge>
                          )}
                        </Flex>
                        <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginTop="0px" marginBottom="16px">
                          Material is supplied by a third party. This PO covers installation labor only.
                        </P>

                        {/* Provider supply summary (F4): the supply method
                            and the one material location that matters for it
                            (pickup depot, yard, or job-site delivery). Service
                            type + product were removed here as redundant - the
                            line items table already establishes both. Generic
                            "Supplier"; no client identity. */}
                        {method && (
                          <Flex
                            sx={{
                              flexDirection: "column",
                              gap: "12px",
                              border: `1px solid ${themevals.colors.inputOutline}`,
                              borderRadius: "6px",
                              background: themevals.colors.base_100,
                              padding: "14px 16px",
                              marginBottom: "20px",
                            }}
                          >
                            {method && (
                              <Flex sx={{ flexDirection: "column" }}>
                                <Label sx={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>Supply Method</Label>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#000", marginTop: "3px" }}>{methodLabel}</Box>
                              </Flex>
                            )}
                            {materialLocationLabel && materialLocationAddr && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}`, paddingTop: "12px" }}>
                                <Label sx={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>{materialLocationLabel}</Label>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#000", marginTop: "3px" }}>{materialLocationAddr}</Box>
                              </Flex>
                            )}
                            {hideYardAddrForProvider && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}`, paddingTop: "12px" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500, color: themevals.colors.dkgrey }}>Material is delivered to your yard.</Box>
                              </Flex>
                            )}
                            {method === "delivery_to_site" && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}`, paddingTop: "12px" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500, color: themevals.colors.dkgrey }}>Material is delivered to the job site above.</Box>
                              </Flex>
                            )}
                            {method === "delivery_to_site" && Boolean(po.onSiteLoadingRequired) && (
                              <Flex sx={{ flexDirection: "column", borderTop: `1px solid ${themevals.colors.inputOutline}`, paddingTop: "12px" }}>
                                <Label sx={{ fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>On-site loading</Label>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: "#000", marginTop: "3px" }}>Required</Box>
                              </Flex>
                            )}
                          </Flex>
                        )}

                        {canEdit && (
                          <Flex sx={{ flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
                            {[
                              { key: "pickup_from_fibar", label: "Pickup from supplier", sub: "Installer collects from the supplier's depot." },
                              { key: "delivery_to_site", label: "Supplier delivers to site", sub: "On-site loading may be required." },
                              { key: "delivery_to_yard", label: "Supplier delivers to yard", sub: "Supplier delivers to the installer's yard." },
                            ].map((opt) => {
                              const on = method === opt.key;
                              return (
                                <Box
                                  key={opt.key}
                                  onClick={() => !savingSupply && saveSupply({
                                    supplyMethod: opt.key,
                                    onSiteLoadingRequired: opt.key === "delivery_to_site" ? !!po.onSiteLoadingRequired : false,
                                  })}
                                  sx={{
                                    cursor: savingSupply ? "default" : "pointer",
                                    border: `1px solid ${on ? themevals.colors.black : themevals.colors.mdgrey}`,
                                    borderRadius: "6px",
                                    padding: "8px 10px",
                                    background: on ? themevals.colors.base_100 : "#fff",
                                  }}
                                >
                                  <P fontFamily="Poppins" fontSize="13px" fontWeight={on ? 600 : 400} marginTop="0px" marginBottom="0px">
                                    {opt.label}
                                  </P>
                                  <P fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey} marginTop="3px" marginBottom="0px">
                                    {opt.sub}
                                  </P>
                                </Box>
                              );
                            })}
                          </Flex>
                        )}

                        {method === "pickup_from_fibar" && canEdit && (
                          <Flex sx={{ flexDirection: "column", marginBottom: "16px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>
                              Supplier pickup location <Box as="span" sx={{ color: themevals.colors.danger }}>*</Box>
                            </Label>
                            <Box sx={{ marginTop: "5px" }}>
                                <input
                                  type="text"
                                  defaultValue={[addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", ")}
                                  placeholder="Depot address"
                                  onBlur={(e) => {
                                    const raw = e.target.value.trim();
                                    if (raw === ([addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", "))) return;
                                    // Store as a single line1 for now; team can refine.
                                    saveSupply({ supplySourceAddress: raw ? { line1: raw } : null });
                                  }}
                                  style={{
                                    width: "100%", padding: "8px 10px", fontFamily: "Poppins", fontSize: "13px",
                                    border: `1px solid ${pickupAddrMissing ? themevals.colors.danger : themevals.colors.mdgrey}`, borderRadius: "6px",
                                  }}
                                />
                                {pickupAddrMissing && (
                                  <P fontFamily="Poppins" fontSize="11px" color={themevals.colors.danger} marginTop="5px" marginBottom="0px">
                                    A pickup location is required before sending this PO.
                                  </P>
                                )}
                            </Box>
                          </Flex>
                        )}

                        {method === "delivery_to_site" && canEdit && (
                          <Flex sx={{ alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                            <Box
                                onClick={() => !savingSupply && saveSupply({ onSiteLoadingRequired: !po.onSiteLoadingRequired })}
                                sx={{
                                  cursor: savingSupply ? "default" : "pointer",
                                  width: "16px", height: "16px", borderRadius: "3px", flex: "0 0 auto",
                                  border: `1.5px solid ${po.onSiteLoadingRequired ? themevals.colors.black : themevals.colors.mdgrey}`,
                                  background: po.onSiteLoadingRequired ? themevals.colors.black : "#fff",
                                }}
                              />
                            <P fontFamily="Poppins" fontSize="13px" color={themevals.colors.black} marginTop="0px" marginBottom="0px">
                              On-site loading required
                            </P>
                          </Flex>
                        )}
                      </Box>
                    </>
                  );
                })()}
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                {/* Third-Party Sync (like Providers) */}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Third-Party Sync
                  </H5>
                  {(() => {
                    const lastSyncSuccessful = po?.external?.zoho?.lastSyncSuccessful;
                    const lastSyncColor = lastSyncSuccessful === undefined ? themevals.colors.dkgrey : lastSyncSuccessful ? themevals.colors.success : themevals.colors.danger;
                    const lastSyncText = po?.external?.zoho?.lastSync ? formatLastSync(po.external.zoho.lastSync) : "N/A";
                    return (
                      <>
                        <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Sync</Label>
                          <P fontFamily={"Poppins"} color={lastSyncColor} fontSize={"14px"} fontWeight={600} marginTop={"5px"} marginBottom={"0px"}>
                            {lastSyncText}
                          </P>
                        </Flex>
                        {/* Resync is staff only (2026-10-20) */}
                        {user?.accessLevel !== "isProvider" && (
                        <BasicButton scheme="secondaryOutline" type="button" $iconHeight="14px" $iconWidth="14px" $loadingColor="#000" $submitting={confirmSubmit} keepText={true} onClick={submitSync}>
                            {!confirmSubmit && <ReloadIcon fill={"#000"} />}
                            Resync
                          </BasicButton>
                        )}
                      </>
                    );
                  })()}
                </Box>
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "30px" }} />
                {po.project?.location?.id && (
                  <>
                    {/*
                     * Session 30: split the map section by viewer role.
                     * Company sees the full SiteMapSection with attach
                     * controls. Provider (isProvider) sees only the
                     * SnapshotSelectionDisplay, and only when snapshots
                     * are attached — otherwise the whole section
                     * (including trailing divider) disappears. No orphan
                     * divider, no "Open map" 403 button, no metadata card
                     * for a map with no snapshots.
                     */}
                    {user.accessLevel !== "isProvider" && (
                      <>
                        <SiteMapSection
                          locationId={po.project.location.id}
                          mulchMapId={po.mulchMapId ?? null}
                          recordType="purchase_order"
                          recordId={po.id}
                          onAttachChanged={() => reloadPO()}
                        />
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      </>
                    )}
                    {user.accessLevel === "isProvider" && (po.mulchMapSnapshotIds?.length ?? 0) > 0 && (
                      <>
                        <SnapshotSelectionDisplay
                          recordType="purchase_order"
                          recordId={po.id}
                          heading="Site maps included with this PO"
                        />
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      </>
                    )}
                  </>
                )}
                {user.accessLevel === "isCompany" && (
                  <Box sx={{ paddingRight: "30px" }}>
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                      Provider
                    </H5>
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Provider Name</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {po.provider.name}
                      </P>
                    </Flex>
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Depot</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {(() => {
                          if (!po.lineitems || po.lineitems.length === 0) return "";
                          const seen = new Set();
                          const depotNames = po.lineitems.filter((item: any) => item.depot && item.depot.id && !seen.has(item.depot.id) && seen.add(item.depot.id)).map((item: any) => item.depot.name);
                          return depotNames.join(", ");
                        })()}
                      </P>
                    </Flex>
                  </Box>
                )}
                {user.accessLevel === "isProvider" && (
                  <Box sx={{ paddingRight: "30px" }}>
                    <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                      Contacts
                    </H5>
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Name</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {po.project?.siteContactName}
                      </P>
                    </Flex>
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Email</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {po.project?.siteContactEmail ? po.project?.siteContactEmail : "-"}
                      </P>
                    </Flex>
                  </Box>
                )}
                {user.accessLevel === "isCompany" && (
                  <Box sx={{ paddingBottom: "60px" }}>
                    {user.accessLevel !== "isProvider" && (
                      <>
                        <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                        <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                          Danger Zone
                        </H5>
                        <BasicButton
                          scheme="dangerSecondary"
                          styles={{ margin: ".25rem 0.625rem .25rem 0" }}
                          type="button"
                          onClick={() => _confirmCancelPO()}
                          $iconHeight="14px"
                          $iconWidth="14px"
                          $loadingColor={themevals.colors.danger}
                          keepText={true}
                        >
                          <TrashIcon fill={"#A60000"} />
                          Cancel PO
                        </BasicButton>
                      </>
                    )}
                  </Box>
                )}
              </Box>
              {company && (
                <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
                  {/* A15 (PO leg) — consistent "back to work order" banner
                      for a PO a Work Order spawned. Staff-only (never
                      providers); resolves the WO from the PO's quote's
                      RFQ (po.quote.quoteRequest.id, now joined in
                      findById + exposed on PurchaseOrderDTO). NTE
                      suppressed — a PO is a provider cost record, not the
                      pricing-verification surface. Renders nothing for a
                      plain PO. */}
                  {user?.accessLevel === "isCompany" && (
                    <MaintenanceContextBanner
                      rfqId={po.quote?.quoteRequest?.id ?? null}
                      showNte={false}
                    />
                  )}
                  {/* Send PO banner — company users, status New (0), inside content area to match project Next Steps placement */}
                  {user?.accessLevel === "isCompany" && Number(po.status) === 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#DDEDFF",
                        border: "1px solid #BDD7F5",
                        borderRadius: "6px",
                        padding: "12px 16px",
                        marginBottom: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginBottom="2px" marginTop="0px" color="#104E9A">
                          This purchase order has not been sent to the provider
                        </P>
                        <P fontFamily="Poppins" fontSize="12px" marginBottom="0px" marginTop="0px" color="#104E9A">
                          {po.provider?.name || "Provider"} has not received this PO yet. Review the details and send when ready.
                        </P>
                      </Box>
                      <BasicButton
                        scheme="infoOutline"
                        onClick={() => { if (!supplyBlocksSend()) setShowSendPoModal(true); }}
                        styles={{ padding: "4px 12px", fontSize: "12px" }}
                      >
                        Send to Provider
                      </BasicButton>
                    </Box>
                  )}
                  {/*
                    SENT BUT NOT ACCEPTED - the state the provider queue
                    surfaces and the PO page had no answer for. The
                    not-sent banner above handles status 0; this handles
                    what happens after.

                    A REMINDER, NOT A RE-SEND. Re-sending runs
                    `sendPdfPurchaseOrder`, which sets sentDate to NOW,
                    advances status and clears the supply-drift flag -
                    so "unaccepted for 12 days" would silently become
                    "sent today" and the dashboard would reorder itself.
                    The reminder writes nothing to the record.
                  */}
                  {user?.accessLevel === "isCompany" &&
                    Number(po.status) === 1 &&
                    po.sentDate &&
                    !po.acceptedDate && (
                      <Box
                        sx={{
                          backgroundColor: "#FFF8E1",
                          border: "1px solid #E7CC7A",
                          borderRadius: "6px",
                          padding: "12px 16px",
                          marginBottom: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                        }}
                      >
                        <Box>
                          <P fontFamily="Poppins" fontSize="13px" fontWeight="600" marginBottom="2px" marginTop="0px" color="#8A6116">
                            Waiting on the provider to accept
                          </P>
                          <P fontFamily="Poppins" fontSize="12px" marginBottom="0px" marginTop="0px" color="#8A6116">
                            {reminderResult
                              ? reminderResult
                              : "Sent " +
                                moment(po.sentDate).fromNow() +
                                ". A reminder emails " +
                                (po.provider?.name || "the provider") +
                                " without changing this order."}
                          </P>
                        </Box>
                        {!reminderResult && (
                          <BasicButton
                            scheme="infoOutline"
                            disabled={sendingReminder}
                            onClick={async () => {
                              setSendingReminder(true);
                              try {
                                const res = await sendReminder("purchase_order", po.id);
                                setReminderResult(
                                  res.sent
                                    ? "Reminder sent to " + (res.recipient || "the provider") + "."
                                    : "A reminder already went out in the last 24 hours.",
                                );
                              } catch (e: any) {
                                toast.add(
                                  e?.response?.data?.message || "Could not send that reminder.",
                                  "var(--theme-ui-colors-danger)",
                                );
                              } finally {
                                setSendingReminder(false);
                              }
                            }}
                            styles={{ padding: "4px 12px", fontSize: "12px", whiteSpace: "nowrap" }}
                          >
                            {sendingReminder ? "Sending..." : "Send reminder"}
                          </BasicButton>
                        )}
                      </Box>
                    )}
                  {activeTab === "overview" && (
                    <>
                      <AcceptanceBanner
                        accepted={Number(po?.status) >= 2 && Number(po?.status) !== 4 && Number(po?.status) !== 6}
                        disclosures={disclosures}
                        termsAcknowledgedAt={termsAcknowledgedAt}
                        termsAcknowledgedBy={termsAcknowledgedBy}
                        notesReviewedCount={notesReviewedCount}
                      />
                      <Overview
                        isCompany={user.role !== null ? true : false}
                        company={company}
                        po={po}
                        reloadPO={reloadPO}
                        onRequireAcknowledgment={() => setShowAckModal(true)}
                        // view.tsx holds the post-acknowledgment value in
                        // its own state and never writes it back onto `po`,
                        // so Overview has to be told — otherwise Accept
                        // would bounce back to the acknowledgment modal
                        // forever after the provider had already signed.
                        termsAcknowledgedAt={termsAcknowledgedAt}
                        acceptAfterAck={acceptAfterAck}
                      />
                    </>
                  )}
                  {/* {activeTab === "versions" && <Versions isCompany={user.role !== null ? true : false} />} */}
                  {activeTab === "notes" && <Notes isCompany={user.role !== null ? true : false} po={po} reloadPO={reloadPO} />}
                  {activeTab === "payments" && <Payments isCompany={user.role !== null ? true : false} po={po} reloadPO={reloadPO} />}
                  {activeTab === "files" && <Files isCompany={user.role !== null ? true : false} po={po} reloadPO={reloadPO} />}
                  {activeTab === "invoices" && (
                    <InvoicesList
                      po={po}
                      reloadPO={reloadPO}
                      termsAcknowledgedAt={termsAcknowledgedAt}
                      onRequireAcknowledgment={() => setShowAckModal(true)}
                    />
                  )}
                </Box>
              )}
            </Grid>
          </>
        )}
      </Page>
      {confirmCancelModalOpen && (
        <BasicModal
          title={`Cancel PO?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _submitCancelPO()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              Cancel PO
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to cancel this purchase order?</P>
        </BasicModal>
      )}
      {showSendPoModal && po && (
        <BasicModal
          title="Send PO to Provider?"
          cancel_click={() => setShowSendPoModal(false)}
          cancel_text="Cancel"
          $submitting={sendingPo}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => _submitSendPo()}
              $submitting={sendingPo}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              Send PO
            </BasicButton>
          }
        >
          <Box sx={{ fontFamily: "Poppins", fontSize: "14px", lineHeight: "22px" }}>
            <P lineHeight="22px" marginBottom="12px">This will email the purchase order to the provider and update the status to Sent.</P>
            <Flex sx={{ flexDirection: "column", gap: "8px", backgroundColor: "#F7F8FA", borderRadius: "6px", padding: "12px 16px" }}>
              <Flex sx={{ justifyContent: "space-between" }}>
                <Box sx={{ color: themevals.colors.dkgrey, fontSize: "13px" }}>PO#</Box>
                <Box sx={{ fontWeight: 600, fontSize: "13px" }}>GMO-{String(po.sequence).padStart(5, "0")}</Box>
              </Flex>
              <Flex sx={{ justifyContent: "space-between" }}>
                <Box sx={{ color: themevals.colors.dkgrey, fontSize: "13px" }}>Provider</Box>
                <Box sx={{ fontWeight: 600, fontSize: "13px" }}>{po.provider?.name || "—"}</Box>
              </Flex>
              <Flex sx={{ justifyContent: "space-between" }}>
                <Box sx={{ color: themevals.colors.dkgrey, fontSize: "13px" }}>Total</Box>
                <Box sx={{ fontWeight: 600, fontSize: "13px" }}>
                  {po.lineitems && po.lineitems.length > 0
                    ? "$" + po.lineitems
                        .filter((li: any) => typeof li.version === "number" ? li.version === Number(po.version ?? 1) : true)
                        .reduce((sum: number, li: any) => sum + (Number(li.quantity || 0) * Number(li.cost || 0)), 0)
                        .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "—"}
                </Box>
              </Flex>
              <Flex sx={{ justifyContent: "space-between" }}>
                <Box sx={{ color: themevals.colors.dkgrey, fontSize: "13px" }}>Project</Box>
                <Box sx={{ fontWeight: 600, fontSize: "13px" }}>{po.project?.projectName || "—"}</Box>
              </Flex>
            </Flex>
          </Box>
        </BasicModal>
      )}

      {showAckModal && po && (
        <AcknowledgmentModal
          po={po}
          disclosures={disclosures}
          onAcknowledged={(payload) => {
            setTermsAcknowledgedAt(payload.termsAcknowledgedAt);
            setTermsAcknowledgedBy(payload.termsAcknowledgedBy);
            setNotesReviewedCount(payload.notesReviewedCount);
            setShowAckModal(false);
            setAcceptAfterAck((n) => n + 1);
            toast.add(
              "Terms acknowledged. Confirm below to accept the PO.",
              "var(--theme-ui-colors-success)"
            );
          }}
          onReject={() => {
            // §4.3 — reject does NOT require prior acknowledgment.
            // Close the modal, then let the existing Overview reject
            // flow take over on the next click. The provider sees the
            // reject button in Overview's ActionRow with no gate.
            setShowAckModal(false);
            toast.add(
              "You can now reject this PO from the actions below.",
              "var(--theme-ui-colors-info)"
            );
          }}
          onDismiss={() => {
            // Session 76 — let them read the actual PO before
            // committing. Acceptance stays gated server-side on
            // termsAcknowledgedAt; Overview catches the 409 and calls
            // onRequireAcknowledgment, which re-opens this modal.
            setShowAckModal(false);
            toast.add(
              "Review the full PO below. You'll be asked to acknowledge the terms when you accept.",
              "var(--theme-ui-colors-info)"
            );
          }}
        />
      )}
    </>
  );
};

export default PurchaseOrder;
